/**
 * Error codes mapping from backend to user-friendly messages.
 *
 * The backend returns machine-readable error codes in the `detail` field.
 * This module maps those codes to user-friendly messages for display.
 *
 * The error codes follow the pattern: {DOMAIN}_{ACTION}_{REASON}
 */

/**
 * User-friendly error messages mapped to backend error codes.
 * Keys are the exact error codes returned by the backend.
 */
export const API_ERROR_MESSAGES: Record<string, string> = {
  // =========================================================================
  // Authentication & Authorization Errors
  // =========================================================================
  LOGIN_BAD_CREDENTIALS: 'Invalid email or password. Please try again.',
  LOGIN_USER_NOT_VERIFIED:
    'Please verify your email before logging in. Check your inbox for the verification link.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  INVALID_TOKEN: 'Your session is invalid. Please log in again.',
  INVALID_REFRESH_TOKEN: 'Your session has expired. Please log in again.',
  VERIFY_USER_BAD_TOKEN:
    'This verification link is invalid or has expired. Please request a new one.',
  VERIFY_USER_ALREADY_VERIFIED: 'Your email is already verified. You can log in now.',
  RESET_PASSWORD_BAD_TOKEN:
    'This password reset link is invalid or has expired. Please request a new one.',
  RESET_PASSWORD_INVALID_PASSWORD: 'Please choose a stronger password.',

  // =========================================================================
  // User Registration Errors
  // =========================================================================
  REGISTER_USER_ALREADY_EXISTS: 'This email is already registered. Please log in instead.',
  PHONE_NUMBER_ALREADY_EXISTS:
    'This phone number is already registered. Please use a different number or log in.',
  REGISTER_INVALID_PASSWORD: 'Password does not meet the security requirements.',
  EMAIL_ALREADY_EXISTS: 'This email is already registered. Please log in instead.',

  // =========================================================================
  // User Profile Errors
  // =========================================================================
  USER_NOT_FOUND: 'User not found.',

  // =========================================================================
  // Caregiver Profile Errors
  // =========================================================================
  CAREGIVER_PROFILE_NOT_FOUND: 'Caregiver profile not found.',
  CAREGIVER_PROFILE_INCOMPLETE: 'Please complete your profile before proceeding.',

  // =========================================================================
  // Supervisor Errors
  // =========================================================================
  SUPERVISOR_SEAT_LIMIT_REACHED:
    'Your supervisor plan has reached its seat limit. Go to the Manage Plan tab to add another seat before creating another caregiver.',

  // =========================================================================
  // Availability Errors
  // =========================================================================
  AVAILABILITY_SLOT_NOT_FOUND: 'This availability slot no longer exists.',
  AVAILABILITY_SLOT_OVERLAP: 'This time slot overlaps with an existing availability.',
  AVAILABILITY_INVALID_TIME_RANGE: 'End time must be after start time.',
  AVAILABILITY_IN_PAST: 'Cannot create availability for past dates.',

  // =========================================================================
  // Booking Errors
  // =========================================================================
  BOOKING_NOT_FOUND: 'Booking not found.',
  BOOKING_SLOT_UNAVAILABLE: 'This time slot is no longer available. Please select another time.',
  BOOKING_CONFLICT: 'This booking conflicts with an existing appointment.',
  BOOKING_INVALID_TIME_RANGE: 'The booking time must fall within the availability slot.',
  BOOKING_ALREADY_CANCELLED: 'This booking has already been canceled.',
  BOOKING_ALREADY_COMPLETED: 'This booking has already been completed.',

  // =========================================================================
  // Booking Request Errors
  // =========================================================================
  BOOKING_REQUEST_NOT_FOUND: 'Booking request not found.',
  BOOKING_REQUEST_EXPIRED: 'This booking request has expired.',
  BOOKING_REQUEST_ALREADY_PROCESSED: 'This booking request has already been processed.',
  PUBLIC_BOOKING_TOKEN_EXPIRED:
    'This booking access link expired. Verify your phone number to continue.',

  // =========================================================================
  // Validation Errors
  // =========================================================================
  VALIDATION_ERROR: 'Please check your input and try again.',
  VALIDATION_INVALID_PHONE: 'Please enter a valid 10-digit phone number.',
  VALIDATION_INVALID_EMAIL: 'Please enter a valid email address.',
  VALIDATION_REQUIRED_FIELD: 'This field is required.',

  // =========================================================================
  // Generic Errors
  // =========================================================================
  DATABASE_INTEGRITY_ERROR: 'A data conflict occurred. Please try again.',
  INTERNAL_SERVER_ERROR: 'Something went wrong on our end. Please try again later.',
  NOT_IMPLEMENTED: 'This feature is coming soon.',
  RATE_LIMIT_EXCEEDED: 'Too many requests. Please wait a moment and try again.',
};

/**
 * Default error message when error code is not recognized
 */
export const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Get a user-friendly message for an error code.
 * Falls back to the error code itself if no mapping exists,
 * or to a default message if the code looks like an internal code.
 */
export function getMessageForErrorCode(errorCode: string): string {
  // Check for exact match
  if (API_ERROR_MESSAGES[errorCode]) {
    return API_ERROR_MESSAGES[errorCode];
  }

  // If the code looks like an internal code (contains underscores), use default
  if (errorCode.includes('_') && errorCode === errorCode.toUpperCase()) {
    return DEFAULT_ERROR_MESSAGE;
  }

  // Otherwise, the code might already be a message
  return errorCode;
}

/**
 * Check if an error message contains any known error code
 * and return the user-friendly message if found.
 */
export function translateErrorMessage(message: string): string {
  // Check if the message itself is an error code
  if (API_ERROR_MESSAGES[message]) {
    return API_ERROR_MESSAGES[message];
  }

  // Check if any error code is contained in the message
  for (const [code, friendlyMessage] of Object.entries(API_ERROR_MESSAGES)) {
    if (message.includes(code)) {
      return friendlyMessage;
    }
  }

  // Return original message if no code found
  return message;
}
